import {Portal, type PortalProps} from '@primer/react'
import {clsx} from 'clsx'
import {useSyncExternalStore} from 'react'

import styles from './ControlledTooltip.module.css'

/**
 * This file is a copy of the file at: {@link https://github.com/primer/react/blob/main/src/Tooltip.tsx} with
 * some modifications to allow the tooltip to be controlled by the parent component.
 */

type TooltipDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'

const DIRECTION_CLASSES: Record<TooltipDirection, string> = {
  n: styles.tooltippedN,
  s: styles.tooltippedS,
  e: styles.tooltippedE,
  w: styles.tooltippedW,
  ne: styles.tooltippedNe,
  nw: styles.tooltippedNw,
  se: styles.tooltippedSe,
  sw: styles.tooltippedSw,
}

const ALIGN_CLASSES: Record<string, string> = {
  left: styles.tooltippedAlignLeft2,
  right: styles.tooltippedAlignRight2,
}

export type ControlledTooltipProps = Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> & {
  direction?: TooltipDirection
  text?: string
  noDelay?: boolean
  align?: 'left' | 'right'
  wrap?: boolean
  /**
   * Whether the tooltip is open or closed
   */
  open?: boolean
  /**
   * Props passed into the portal
   */
  portalProps?: PortalProps
  ref?: React.Ref<HTMLSpanElement>
}

const noop = () => () => undefined
const getServerSideState = () => false
const getClientSideState = () => true
/**
 * An extended `@primer/react` tooltip (based on version 35.21 and prior) which allows for external control
 * using an `open` prop
 * @deprecated Use the Primer React Tooltip component instead for improved accessibility and usability: https://primer.style/product/components/tooltip/
 */
export function ControlledTooltip({
  ref,
  direction = 'n',
  className,
  text,
  noDelay,
  align,
  wrap,
  open = false,
  portalProps = {},
  ...rest
}: ControlledTooltipProps) {
  const isClientRender = useSyncExternalStore(noop, getClientSideState, getServerSideState)
  const classes = clsx(
    styles.tooltipBase,
    DIRECTION_CLASSES[direction],
    align && ALIGN_CLASSES[align],
    noDelay && styles.tooltippedNoDelay,
    wrap && styles.tooltippedMultiline,
    open && styles.tooltippedOpen,
    styles.fixedPosition,
    className,
  )
  if (!isClientRender) return null
  return (
    <Portal {...portalProps}>
      <span
        ref={ref}
        role="tooltip"
        aria-label={text}
        data-visible-text={text || rest['aria-label']}
        {...rest}
        className={classes}
      />
    </Portal>
  )
}
